import { Box, Paper, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPagination, GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { Item } from '../util/Item';
import { BatchTask } from "../util/Batch";
import { Util } from "../util/Util"
import { GridApiCommunity } from "@mui/x-data-grid/internals";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSocket } from "../providers/AppSocketContext";
import { applyQuantityStepperColumns, quantityStepperGridSx } from "./quantityStepperColumns";
import { CatalogItemNameCell } from "./CatalogItemActions";
import {
	buildBundleQuantityByItemId,
	buildItemCatalogMap,
	buildQuantityMaxByRowId,
	buildTaskQuantityByItemId,
	isNegativeStockAllowed,
	isItemMetered,
	shouldEnforceStockQuantity,
} from "./quantityStepperStock";
import { MONEY_LIMITS, QTY_LIMITS, isWholeNumber, qtyMin, addMoney, roundQty } from "../util/quantity";
import { useBufferedTaskItems } from "../hooks/useBufferedTaskItems";
import { useActiveTaskStatusTotal } from "../hooks/useActiveTaskStatusTotal";
import { ItemTableGridApi } from "../util/itemTableFlush";

type LineNameMode = "sale" | "strict";

interface Props<T extends Item, U extends BatchTask<T>> {
	bundle?: any;
	activeTask: U | undefined;
	setPendingTasks: Dispatch<SetStateAction<U[]>>;
	columns: GridColDef<T>[];
	itemConstructor: new (data: Partial<T>) => T; // Constructor for T
	items: any[]; // List of all items with stock information
	checkStock?: boolean; // Whether to check stock levels
	quantityStepper?: boolean;
	quantityMin?: number;
	lineNameMode?: LineNameMode;
	allowPosAliasing?: boolean;
	/** Hide the count/total footer (e.g. POS visual cart bar already shows totals). */
	hideFooter?: boolean;
	apiRef: ReturnType<typeof useGridApiRef<GridApiCommunity>>;
	getMinSellPrice?: (row: T) => number;
}

const ItemTable = <T extends Item, U extends BatchTask<T>>(
	{
		items,
		bundle,
		activeTask,
		setPendingTasks,
		columns,
		itemConstructor,
		checkStock = true,
		quantityStepper = true,
		quantityMin = 1,
		lineNameMode = "strict",
		allowPosAliasing = true,
		hideFooter = false,
		apiRef,
		getMinSellPrice,
	}: Props<T, U>
) => {

	const { t } = useTranslation();
	const { appSettings } = useAppSocket();

	const bufferedApiRef = apiRef as { current: ItemTableGridApi<T> | null };
	const { gridRows, gridRowsRef, replaceGridRows } = useBufferedTaskItems<T>({
		activeTask,
		setPendingTasks,
		apiRef: bufferedApiRef,
	});

	const itemsById = useMemo(() => buildItemCatalogMap(items), [items]);
	const taskQtyByItemId = useMemo(
		() => buildTaskQuantityByItemId(gridRows),
		[gridRows],
	);
	const bundleQtyByItemId = useMemo(
		() => buildBundleQuantityByItemId(bundle),
		[bundle],
	);
	const quantityMaxByRowId = useMemo(
		() => buildQuantityMaxByRowId({
			rows: gridRows,
			itemsById,
			taskQtyByItemId,
			bundleQtyByItemId,
			checkStock,
			allowNegativeStock: isNegativeStockAllowed(appSettings?.ALLOW_NEGATIVE_STOCK?.value),
		}),
		[gridRows, itemsById, taskQtyByItemId, bundleQtyByItemId, checkStock, appSettings?.ALLOW_NEGATIVE_STOCK?.value],
	);

	const handleProcessRowUpdate = useCallback((newRow: Partial<T>, oldRow: T): T => {
		const currentRows = gridRowsRef.current;

		if (newRow.quantity !== oldRow.quantity) {
			const catalogItem = itemsById.get(oldRow.id);
			const isMetered = isItemMetered(catalogItem, true);
			const bundleQty = bundleQtyByItemId.get(oldRow.id) ?? 0;
			const taskQty = taskQtyByItemId.get(oldRow.id) ?? 0;
			const activeStock = roundQty((catalogItem?.Stock ?? 0) + bundleQty + oldRow.quantity - taskQty);

			const newQuantity = newRow.quantity;
			const fractional = Boolean((oldRow as { isFractional?: boolean }).isFractional)
				|| catalogItem?.IsFractional === true;

			if (newQuantity === undefined || newQuantity < qtyMin(fractional) || newQuantity > QTY_LIMITS.max) {
				toast.error(t("common:validation.quantityRange", { max: Util.formatNumber(QTY_LIMITS.max) }));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId } as T; // prevent total and uniqueId from being lost and having #undefined ID
				return oldRow;
			} else if (!fractional && !isWholeNumber(newQuantity)) {
				toast.error(t("common:validation.quantityMustBeWhole"));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId } as T;
				return oldRow;
			} else if (
				shouldEnforceStockQuantity({
					isMetered,
					checkStock,
					allowNegativeStock: isNegativeStockAllowed(appSettings?.ALLOW_NEGATIVE_STOCK?.value),
				})
				&& newQuantity > activeStock
			) {
				toast.error(t("common:validation.insufficientStockAvailable", { available: Util.formatNumber(activeStock) }));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId } as T; // prevent total and uniqueId from being lost and having #undefined ID
				return oldRow;
			}
		}

		if (newRow.price !== oldRow.price) {
			const newAmount = newRow.price;

			if (newAmount === undefined || newAmount < 0 || newAmount > MONEY_LIMITS.max) {
				toast.error(t("common:validation.amountRange", { max: Util.formatNumber(MONEY_LIMITS.max) }));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId } as T; // prevent total and uniqueId from being lost and having #undefined ID
				return oldRow;
			}
			const minSell = getMinSellPrice?.(oldRow) ?? 0;
			if (minSell > 0 && newAmount < minSell) {
				toast.error(t("common:validation.priceBelowMinSell", {
					min: Util.formatNumber(minSell),
					item: oldRow.item,
				}));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId } as T;
				return oldRow;
			}
		}

		const mergedRow = new itemConstructor({ ...oldRow, ...newRow });

		if (newRow.item !== oldRow.item || (lineNameMode === "sale" && newRow.id !== undefined && newRow.id !== oldRow.id)) {
			if (lineNameMode === "sale" && !allowPosAliasing) {
				toast.error(t("common:validation.posAliasingNotAllowed", { item: oldRow.item }));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId } as T;
				return oldRow;
			}

			if (lineNameMode === "sale") {
				const catalogMatch = items.find((catalog: any) =>
					Util.compareStrings(catalog.ItemName || "", mergedRow.item || "")
				);
				if (catalogMatch && catalogMatch.id !== mergedRow.id) {
					mergedRow.id = catalogMatch.id;
					mergedRow.item_code = catalogMatch.ItemCode ?? mergedRow.item_code;
					mergedRow.item = catalogMatch.ItemName ?? mergedRow.item;
					mergedRow.price = catalogMatch.SellingPrice ?? mergedRow.price;
					mergedRow.stock = catalogMatch.Stock ?? mergedRow.stock;
					mergedRow.category = catalogMatch.Category ?? mergedRow.category;
					(mergedRow as Item).minSellPrice = Number(catalogMatch.MinSellPrice) > 0
						? Number(catalogMatch.MinSellPrice)
						: 0;
				}
			} else {
				toast.error(t("common:validation.noNameModification", { item: oldRow.item }));
				oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId } as T;
				return oldRow;
			}
		}

		const existingIndex = currentRows.findIndex(r => Util.compareStrings(r.uniqueId, mergedRow.uniqueId));
		const indexToRemove = currentRows.findIndex(r => Util.compareStrings(r.uniqueId, oldRow.uniqueId));
		const willMerge = existingIndex > -1 && existingIndex !== indexToRemove;
		const itemIdChanged = mergedRow.id !== oldRow.id;

		if (willMerge) {
			mergedRow.quantity = roundQty(mergedRow.quantity + (currentRows[existingIndex]?.quantity || 0));
		}

		if (checkStock && (itemIdChanged || willMerge)) {
			const catalogItem = itemsById.get(mergedRow.id);
			const isMetered = isItemMetered(catalogItem, true);

			if (
				shouldEnforceStockQuantity({
					isMetered,
					checkStock,
					allowNegativeStock: isNegativeStockAllowed(appSettings?.ALLOW_NEGATIVE_STOCK?.value),
				})
			) {
				const excludeUniqueIds = new Set<string>([oldRow.uniqueId]);
				if (willMerge) {
					excludeUniqueIds.add(mergedRow.uniqueId);
				}

				let otherRowsQty = 0;
				for (const row of currentRows) {
					if (excludeUniqueIds.has(row.uniqueId)) continue;
					if (row.id === mergedRow.id) {
						otherRowsQty = roundQty(otherRowsQty + row.quantity);
					}
				}

				const bundleQty = bundleQtyByItemId.get(mergedRow.id) ?? 0;
				const available = (catalogItem?.Stock ?? 0) + bundleQty;
				const projectedTotal = otherRowsQty + mergedRow.quantity;

				if (projectedTotal > available) {
					toast.error(t("common:validation.insufficientStockAvailable", {
						available: Util.formatNumber(Math.max(available - otherRowsQty, 0)),
					}));
					oldRow = { ...oldRow, total: oldRow.total, uniqueId: oldRow.uniqueId } as T;
					return oldRow;
				}
			}
		}

		if (willMerge) {
			if (apiRef.current) {
				const oldIndex = apiRef.current.getRowIndexRelativeToVisibleRows(oldRow.uniqueId);
				const newIndex = apiRef.current.getRowIndexRelativeToVisibleRows(mergedRow.uniqueId);
				const rowIndex = newIndex + ((oldIndex > newIndex) ? 1 : 0);
				toast.info(t("common:info.mergedWithRow", { row: rowIndex, quantity: mergedRow.quantity }));
			}
		}

		const nextRows = currentRows
			.map((item, idx) =>
				idx === existingIndex || existingIndex === -1 && indexToRemove === idx ? mergedRow :
					existingIndex > -1 && idx === indexToRemove && existingIndex !== indexToRemove ? null : item
			)
			.filter((item): item is T => item !== null);

		replaceGridRows(nextRows);

		return { ...mergedRow, total: mergedRow.total, uniqueId: mergedRow.uniqueId } as T; // Ensure total and uniqueId are included
	}, [
		apiRef,
		bundleQtyByItemId,
		checkStock,
		gridRowsRef,
		itemConstructor,
		itemsById,
		replaceGridRows,
		t,
		taskQtyByItemId,
		items,
		lineNameMode,
		allowPosAliasing,
		appSettings?.ALLOW_NEGATIVE_STOCK?.value,
		getMinSellPrice,
	]);

	const onQuantityChangeByRowId = useCallback((rowId: string, newValue: number) => {
		const row = gridRowsRef.current.find((item) => item.uniqueId === rowId);
		if (!row) return;
		handleProcessRowUpdate({ ...row, quantity: newValue }, row);
	}, [gridRowsRef, handleProcessRowUpdate]);

	const isPos = lineNameMode === "sale";

	const columnsWithHoverActions = useMemo(() => {
		return columns.map((col) => {
			if (col.field !== "item") return col;
			const originalRenderCell = col.renderCell;
			return {
				...col,
				renderCell: (params: GridRenderCellParams<T>) => {
					const cellContent = originalRenderCell ? originalRenderCell(params) : (params.value ?? "");
					const itemId = params.row.id;
					const itemName = params.row.item || String(params.value || "");
					return (
						<CatalogItemNameCell itemId={itemId} itemName={itemName} isPos={isPos}>
							{cellContent}
						</CatalogItemNameCell>
					);
				},
			};
		});
	}, [columns, isPos]);

	const columnsToRender = useMemo(
		() => quantityStepper
			? applyQuantityStepperColumns(columnsWithHoverActions, {
				quantityMin,
				quantityMaxByRowId,
				onQuantityChangeByRowId,
				getIsFractional: (row) => Boolean((row as { isFractional?: boolean }).isFractional),
			})
			: columnsWithHoverActions,
		[columnsWithHoverActions, onQuantityChangeByRowId, quantityMaxByRowId, quantityMin, quantityStepper],
	);

	const paginationModel = useMemo(() => ({ page: 0, pageSize: 100 }), []);

	const gridTotal = useMemo(
		() => gridRows.reduce((sum, item) => addMoney(sum, item.total), 0),
		[gridRows],
	);

	// Classic POS owns the footer total via the table footer.
	// Visual/Split pass hideFooter and publish totals from PointOfSale so list/table
	// cart modes stay consistent (ItemTable unmounts in list view).
	useActiveTaskStatusTotal(gridTotal, gridRows.length, Boolean(activeTask) && !hideFooter);

	const footerLabel = useMemo(
		() => t("common:ui.labels.count", {
			num: Util.formatNumber(gridRows.length),
			total: Util.formatCurrency(gridTotal, appSettings?.BIZ_CURRENCY?.value || "USD"),
		}),
		[appSettings?.BIZ_CURRENCY?.value, gridRows.length, gridTotal, t],
	);

	return (
		<Paper elevation={24} sx={{ width: '100%' }}>
			<DataGrid<T>
				apiRef={apiRef}
				rows={gridRows}
				columns={columnsToRender}
				density="compact"
				getRowId={(row) => row.uniqueId}
				initialState={{ pagination: { paginationModel } }}
				pageSizeOptions={[100, 150, 200, 500, 1000, 2000, 3000, 4000, 5000, 10000]}
				pagination
				processRowUpdate={handleProcessRowUpdate}
				sx={{
					'& .MuiDataGrid-columnHeaders': {
						fontWeight: 'bolder',
						fontSize: 16,
					},
					'& .MuiDataGrid-columnHeaderTitle': {
						fontWeight: 900 + '!important',
					},
					'& .MuiDataGrid-row:hover .catalog-item-option-actions': {
						opacity: 1,
						pointerEvents: "auto",
					},
					...quantityStepperGridSx,
				}}
				hideFooter={hideFooter}

				slots={
					hideFooter
						? undefined
						: {
							footer: () => (
								<Box sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									padding: '8px 16px',
									borderTop: '1px solid rgba(224, 224, 224, 1)',
								}}>
									<Typography
										variant="subtitle1"
										fontWeight="bold"
										sx={{ padding: 1 }}
									>
										{footerLabel}
									</Typography>

									{gridRows.length > 100 && <GridPagination />}
								</Box>
							)
						}
				}
			/>
		</Paper>
	);
};

export default ItemTable;